import { navigateApp } from './appNavigation.ts'

/**
 * Same-origin app path an in-app notification is allowed to open.
 *
 * Only a root-relative path is accepted, and `//host` is rejected because the
 * browser reads it as a protocol-relative URL to another origin.
 */
export function notificationTargetPath(url: unknown): string {
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return ''
  return url
}

type Dependencies = {
  dispatch: (url: string) => void
  navigate: (to: string, options: { replace: boolean }) => boolean
  reload: (to: string) => void
  pathname: () => string
}

const browser: Dependencies = {
  dispatch: url => window.dispatchEvent(
    new CustomEvent('servernox-notification-tap', { detail: { url } }),
  ),
  navigate: (to, options) => navigateApp(to, options),
  reload: to => window.location.assign(to),
  pathname: () => window.location.pathname,
}

/**
 * Open the page an in-app notification points at without reloading the app.
 *
 * The toast used to call `window.location.assign()` unconditionally. Tapping an
 * AI Coding approval while AI Coding was already open therefore tore down the
 * mounted page — its chat tabs, WebSocket and loaded transcript — and rebuilt
 * everything just to land back on the same screen.
 *
 * AI Coding already listens for `servernox-notification-tap` to select the
 * right chat tab and reveal the approval, so the notification only has to
 * announce that intent and move the URL client-side. The event is dispatched
 * *before* the URL moves, matching the native bridge, because listeners
 * consume the notification's query parameters in a microtask scheduled from
 * the event. A real navigation remains the fallback when no router is mounted.
 */
export function openNotificationTarget(url: unknown, dependencies: Dependencies = browser): boolean {
  const target = notificationTargetPath(url)
  if (!target) return false
  dependencies.dispatch(target)
  // Staying on the same page only refines the current view, so it replaces the
  // entry instead of leaving a back step that differs by a query string alone.
  const samePage = target.split(/[?#]/)[0] === dependencies.pathname()
  if (!dependencies.navigate(target, { replace: samePage })) dependencies.reload(target)
  return true
}
